import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import api from "../lib/api";

const STORAGE_KEY = "qureo.inAppNotifications";
const MAX_NOTIFICATIONS = 50;

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const resolveUserId = useCallback(() => {
    const byId = localStorage.getItem("userId");
    if (byId) return byId;
    try {
      return JSON.parse(localStorage.getItem("user") || "{}")?._id || "";
    } catch {
      return "";
    }
  }, []);

  const mapServerNotification = useCallback((item = {}) => ({
    id: item._id || item.id,
    type: item.type,
    title: item.title,
    body: item.body,
    icon: item.icon || "🔔",
    timestamp: item.createdAt || item.timestamp || new Date().toISOString(),
    read: Boolean(item.read),
    data: item.data || {},
    persisted: true,
  }), []);

  const [notifications, setNotifications] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
    } catch {
      // storage quota exceeded — ignore
    }
  }, [notifications]);

  useEffect(() => {
    let isMounted = true;

    const loadFeed = async () => {
      const userId = resolveUserId();
      if (!userId) return;

      try {
        const res = await api.get("/api/notifications/feed", {
          params: { userId, limit: MAX_NOTIFICATIONS },
        });
        const serverItems = Array.isArray(res?.data?.notifications)
          ? res.data.notifications.map(mapServerNotification)
          : [];

        if (isMounted && serverItems.length) {
          setNotifications(serverItems);
        }
      } catch {
        // Fallback to local-only notifications when backend feed is unavailable.
      }
    };

    loadFeed();

    return () => {
      isMounted = false;
    };
  }, [mapServerNotification, resolveUserId]);

  const addNotification = useCallback(({ type, title, body, icon = "🔔", data = {} }) => {
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const nowIso = new Date().toISOString();
    const optimistic = {
      id: tempId,
      type,
      title,
      body,
      icon,
      timestamp: nowIso,
      read: false,
      data,
      persisted: false,
    };

    setNotifications((prev) => [optimistic, ...prev].slice(0, MAX_NOTIFICATIONS));

    const userId = resolveUserId();
    if (!userId || !title) {
      return;
    }

    api
      .post("/api/notifications/events", {
        userId,
        type,
        title,
        body,
        icon,
        data,
      })
      .then((res) => {
        const serverItem = res?.data?.notification;
        if (!serverItem) return;
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === tempId
              ? mapServerNotification(serverItem)
              : n
          )
        );
      })
      .catch(() => {
        // Keep local optimistic notification if backend write fails.
      });
  }, [mapServerNotification, resolveUserId]);

  const markRead = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );

    if (!id || String(id).startsWith("tmp-")) return;
    const userId = resolveUserId();
    if (!userId) return;

    api.patch(`/api/notifications/events/${id}/read`, { userId }).catch(() => {
      // Keep optimistic local read state even if backend call fails.
    });
  }, [resolveUserId]);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

    const userId = resolveUserId();
    if (!userId) return;
    api.patch("/api/notifications/events/read-all", { userId }).catch(() => {
      // Keep optimistic local read state even if backend call fails.
    });
  }, [resolveUserId]);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));

    if (!id || String(id).startsWith("tmp-")) return;
    const userId = resolveUserId();
    if (!userId) return;

    api.delete(`/api/notifications/events/${id}`, {
      params: { userId },
    }).catch(() => {
      // Ignore backend failure; local dismissal still applies.
    });
  }, [resolveUserId]);

  const clearAll = useCallback(() => {
    setNotifications([]);

    const userId = resolveUserId();
    if (!userId) return;
    api.delete("/api/notifications/events", {
      params: { userId },
    }).catch(() => {
      // Ignore backend failure; local clear still applies.
    });
  }, [resolveUserId]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markRead,
        markAllRead,
        removeNotification,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
}
